import Async from "components/DesignSystem/AsyncComponents/Async";
import { useWindowSize } from "hooks/useWindowSize";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import {
  CustomerFilter,
  CustomerFilterType,
} from "components/RevenueCustomer/CustomerFilter";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { HighlightSearchTerm } from "components/DesignSystem/HighlightText";
import Modal from "components/DesignSystem/Modal/Modal";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import { SelectionCell } from "components/DesignSystem/SelectionCell/SelectionCell";
import { SelectionHeaderCell } from "components/DesignSystem/SelectionHeaderCell/SelectionHeaderCell";
import Table from "components/DesignSystem/Table/V2/Table";
import { EmptyInvoiceList } from "components/Illustrations/EmptyInvoiceList";
import { InvoiceCustomerModal } from "components/InvoiceCustomerModal/InvoiceCustomerModal";
import { LockedUi } from "components/LockedUi/LockedUi";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { TableUI } from "components/design/TableUI";
import ConditionalToolTip from "components/design/conditionalToolTip";
import ToolTip from "components/design/toolTip";
import { Contacts as ContactsIcon } from "components/icons/LeftNav/Books/Contacts";
import { Info } from "components/icons/Info";
import { OptionDropdown } from "components/icons/optionDropdown";
import {
  REVENUE_DATA_SOURCE,
  REVENUE_DATA_SOURCE_ICON,
  REVENUE_DATA_SOURCE_OPTIONS,
  REVENUE_DATA_SOURCE_TYPES,
  CUSTOMER_SORT_BY,
  CUSTOMER_SORT_BY_TYPE,
} from "constants/revenueDataSource";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useFilters } from "hooks/useFilter";
import { useModal } from "hooks/useModal";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { parse, stringify } from "qs";
import { useMemo, useState } from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  RowSelectionState,
  SortingState,
} from "react-table-8.10.7";
import {
  useDeleteCustomerMutation,
  useGetAllInvoiceCustomersListQuery,
} from "store/apis/invoices";
import { BackendError } from "types/utils/error";
import { useDebounce } from "utils/debounce";
import { pluralize } from "utils/pluralize";
import { GroupInvoiceCustomer } from "types/Models/invoice";
import { CLICKED_CUSTOMER_LINE_ITEM_IN_TABLE } from "constants/analyticsEvents";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { AddCustomerAddressesModal } from "./AddCustomerAddressesModal";
import { CustomerBulkActions } from "./CustomerBulkActions";

const SELECTION_COLUMN_ID = "SELECTION";

type CustomerActionProps = {
  customer: GroupInvoiceCustomer;
  onDeleted: () => void;
};

const CustomerAction = ({ customer, onDeleted }: CustomerActionProps) => {
  const deleteModal = useModal();
  const editModal = useModal();
  const { alertToast, successToast } = useToast();
  const { uuid: customerId, source } = customer;
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const [deleteCustomer, { isLoading }] = useDeleteCustomerMutation();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.REVENUE
  );

  const isEditable = source === REVENUE_DATA_SOURCE.MANUAL;
  const isDeletable = source === REVENUE_DATA_SOURCE.MANUAL;

  if (!hasWritePermission) return null;

  const onDelete = async () => {
    try {
      await deleteCustomer({
        groupId,
        customerId,
        entityId,
      }).unwrap();
      deleteModal.close();
      onDeleted();
      successToast({ message: "Customer deleted!" });
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as {}
      );
    }
  };

  return (
    <span
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <span>
            <Button size="xsmall" customType="ghost_icon">
              <OptionDropdown color="currentColor" />
            </Button>
          </span>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content align="end" className="!t-min-w-40">
            <Dropdown.Item
              type="ordinary"
              onSelect={() => {
                if (isEditable) {
                  editModal.open();
                }
              }}
              disabled={!isEditable}
            >
              <ConditionalToolTip
                condition={
                  !isEditable &&
                  `Cannot edit ${REVENUE_DATA_SOURCE_OPTIONS[source]} customer`
                }
              >
                <span>Edit</span>
              </ConditionalToolTip>
            </Dropdown.Item>
            <Dropdown.Item
              type="danger"
              onSelect={() => {
                if (isDeletable) {
                  deleteModal.open();
                }
              }}
              disabled={!isDeletable}
            >
              <ConditionalToolTip
                condition={
                  !isDeletable &&
                  `Cannot delete ${REVENUE_DATA_SOURCE_OPTIONS[source]} customer`
                }
              >
                <span>Delete</span>
              </ConditionalToolTip>
            </Dropdown.Item>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>

      <Modal.Root open={deleteModal.isOpen} onOpenChange={deleteModal.close}>
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Are you sure?</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            The selected customer will be permanently deleted. This action
            cannot be undone.
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild disabled={isLoading}>
              <Button disabled={isLoading}>Cancel</Button>
            </Modal.RawClose>
            <Button
              customType="danger"
              onClick={onDelete}
              isLoading={isLoading}
              disabled={isLoading}
            >
              Confirm
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
      <InvoiceCustomerModal
        customerId={editModal.isOpen ? customerId : null}
        setCustomerId={() => editModal.close()}
      />
    </span>
  );
};

const CustomerName = ({ customer }: { customer: GroupInvoiceCustomer }) => {
  const query = useQuery();
  const searchValue = query.get("search");
  const emailText = customer.email || customer?.recipient_emails?.join(", ");

  return (
    <div>
      <div className="t-text-body-sm t-text-text-60">
        <HighlightSearchTerm
          text={customer.name}
          searchTerm={searchValue || ""}
        />
      </div>
      <ToolTip text={emailText}>
        <div className="t-text-body-sm t-text-text-30 t-truncate t-max-w-[200px]">
          <HighlightSearchTerm
            text={emailText}
            searchTerm={searchValue || ""}
          />
        </div>
      </ToolTip>
    </div>
  );
};

export const Source = ({ source }: { source: REVENUE_DATA_SOURCE_TYPES }) => {
  return (
    <div className="t-flex t-justify-center">
      <ConditionalToolTip condition={REVENUE_DATA_SOURCE_OPTIONS[source]}>
        <span>
          {source === REVENUE_DATA_SOURCE.MANUAL ? (
            <span
              className="t-rounded-full t-bg-white t-overflow-hidden t-min-h-6 t-min-w-6 t-max-h-6 t-max-w-6 t-text-neutral-40 t-border-neutral-0 t-border t-border-solid t-text-center"
              role="img"
            >
              <ContactsIcon />
            </span>
          ) : (
            <Avatar
              size="small"
              src={REVENUE_DATA_SOURCE_ICON[source]}
              alt={source}
            />
          )}
        </span>
      </ConditionalToolTip>
    </div>
  );
};

const createColumn = createColumnHelper<GroupInvoiceCustomer>();

const columns = [
  createColumn.display({
    id: SELECTION_COLUMN_ID,
    size: 3,
    header: ({ table }) => (
      <SelectionHeaderCell table={table} ariaLabel="Select all customers" />
    ),
    cell: ({ row }) => (
      <SelectionCell row={row} ariaLabel={`Select ${row.original.name}`} />
    ),
  }),

  createColumn.accessor("uuid", {
    size: 1,
    header: "NO.",
    cell: (info) => {
      return <div className="t-text-center">{info.row.index + 1}</div>;
    },
  }),

  createColumn.accessor("email", {
    id: "NAME",
    size: 26,
    header: (header) => {
      const handler = header.column.getToggleSortingHandler() || (() => {});

      return (
        <div className="t-group t-justify-end">
          <HeadCellContent
            title="NAME"
            align="left"
            sort={{
              nextSortOrder: header.column.getNextSortingOrder(),
              isSorted: header.column.getIsSorted(),
              sortingHandler: handler,
            }}
          />
        </div>
      );
    },
    cell: (info) => <CustomerName customer={info.row.original} />,
    enableSorting: true,
  }),

  createColumn.accessor("paid_amount", {
    id: "PAID_AMOUNT",
    size: 15,
    header: (header) => {
      const handler = header.column.getToggleSortingHandler() || (() => {});

      return (
        <div className="t-group t-justify-end">
          <HeadCellContent
            title="AMOUNT PAID"
            align="right"
            sort={{
              nextSortOrder: header.column.getNextSortingOrder(),
              isSorted: header.column.getIsSorted(),
              sortingHandler: handler,
            }}
          />
        </div>
      );
    },
    cell: (info) => (
      <div className="t-text-right">
        <AmountSuperScript amount={Number(info.getValue() || "0")} />
      </div>
    ),
    enableSorting: true,
  }),

  createColumn.accessor("due_amount", {
    id: "DUE_AMOUNT",
    size: 15,
    header: (header) => {
      const handler = header.column.getToggleSortingHandler() || (() => {});

      return (
        <div className="t-group t-justify-end">
          <HeadCellContent
            title="AMOUNT DUE"
            align="right"
            sort={{
              nextSortOrder: header.column.getNextSortingOrder(),
              isSorted: header.column.getIsSorted(),
              sortingHandler: handler,
            }}
          />
        </div>
      );
    },
    cell: (info) => (
      <div className="t-text-right">
        <AmountSuperScript amount={info.getValue()} />
      </div>
    ),
    enableSorting: true,
  }),

  createColumn.accessor("total_amount", {
    id: "TOTAL_AMOUNT",
    size: 25,
    header: (header) => {
      const handler = header.column.getToggleSortingHandler() || (() => {});

      return (
        <div className="t-group t-justify-end">
          <HeadCellContent
            title="TOTAL INVOICED AMOUNT"
            align="right"
            sort={{
              nextSortOrder: header.column.getNextSortingOrder(),
              isSorted: header.column.getIsSorted(),
              sortingHandler: handler,
            }}
          />
        </div>
      );
    },
    cell: (info) => (
      <div className="t-text-right">
        <AmountSuperScript amount={Number(info.getValue() || "0")} />
      </div>
    ),
    enableSorting: true,
  }),

  createColumn.accessor("source", {
    size: 10,
    header: () => (
      <div className="t-flex t-justify-center t-w-full">SOURCE</div>
    ),
    cell: (info) => <Source source={info.row.original.source} />,
  }),

  createColumn.accessor("uuid", {
    size: 5,
    header: "",
    cell: (info) => (
      <CustomerAction
        customer={info.row.original}
        onDeleted={() => info.row.toggleSelected(false)}
      />
    ),
  }),
];

export const InvoiceCustomers = () => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { url } = useRouteMatch();
  const history = useHistory();
  const [customerId, setCustomerId] = useState<string | null>(null);

  const location = useLocation();
  const parsedSearch = parse(location.search, { ignoreQueryPrefix: true });
  const entityId = useCurrentEntityId();

  const search = stringify(
    {
      entity: parsedSearch.entity,
      company: parsedSearch.company,
    },
    { skipNulls: true, addQueryPrefix: true }
  );

  const { values: filterValues, updateFilter } = useFilters<CustomerFilterType>(
    {
      initialValue: {
        SOURCES: [],
        MIN_AMOUNT_PAID: "",
        MAX_AMOUNT_PAID: "",
        MIN_AMOUNT_DUE: "",
        MAX_AMOUNT_DUE: "",
        MIN_TOTAL_INVOICED: "",
        MAX_TOTAL_INVOICED: "",
        HAS_OVERDUE: false,
        NO_OUTSTANDING_DUE: false,
        NO_PAYMENTS: false,
      },
      useQueryParams: true,
    }
  );

  const query = useQuery();
  const { update } = useUpdateQuery();
  const page = query.get("page");
  const { trackEvent } = useAnalytics();

  const searchValue = query.get("search");
  const searchTerm = useDebounce(searchValue);

  const [sorting, setSorting] = useState<SortingState>([
    { id: "NAME", desc: false },
  ]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.REVENUE
  );

  const columnIdToSortBy: Record<string, CUSTOMER_SORT_BY_TYPE> = {
    PAID_AMOUNT: CUSTOMER_SORT_BY.AMOUNT_PAID,
    DUE_AMOUNT: CUSTOMER_SORT_BY.AMOUNT_DUE,
    TOTAL_AMOUNT: CUSTOMER_SORT_BY.TOTAL_INVOICED_AMOUNT,
    NAME: CUSTOMER_SORT_BY.NAME,
  };

  const sortBy = sorting[0]?.id ? columnIdToSortBy[sorting[0].id] : undefined;
  const sortOrder = sorting[0]?.desc;

  const {
    data: invoiceCustomers,
    isLoading: loadingCustomers,
    isSuccess: loadedCustomers,
    isFetching,
  } = useGetAllInvoiceCustomersListQuery(
    {
      groupId: groupId!,
      entityId: entityId!,
      pageNum: page || "1",
      searchTerm,
      sources: filterValues.SOURCES.join(","),
      sortBy,
      sortOrder: sortBy
        ? sortOrder
          ? ("DESC" as const)
          : ("ASC" as const)
        : undefined,
      minAmountDue: filterValues.MIN_AMOUNT_DUE,
      maxAmountDue: filterValues.MAX_AMOUNT_DUE,
      minAmountPaid: filterValues.MIN_AMOUNT_PAID,
      maxAmountPaid: filterValues.MAX_AMOUNT_PAID,
      minTotalInvoiced: filterValues.MIN_TOTAL_INVOICED,
      maxTotalInvoiced: filterValues.MAX_TOTAL_INVOICED,
      hasOverdueInvoices: filterValues.HAS_OVERDUE,
      hasNoOutstandingDues: filterValues.NO_OUTSTANDING_DUE,
      hasNoPayments: filterValues.NO_PAYMENTS,
    },
    { skip: !groupId || !entityId }
  );

  const customers = useMemo(
    () => invoiceCustomers?.client_customers,
    [invoiceCustomers]
  );

  const table = useReactTable({
    data: customers || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 1,
    },
    state: {
      sorting,
      rowSelection,
      columnVisibility: {
        [SELECTION_COLUMN_ID]: hasWritePermission,
      },
    },
    getRowId: (row) => row.uuid,
    enableRowSelection: (row) =>
      row.original.source === REVENUE_DATA_SOURCE.MANUAL,
    onRowSelectionChange: setRowSelection,
    manualSorting: true,
    enableMultiSort: false,
    enableSortingRemoval: false,
    onSortingChange: setSorting,
  });

  const selectedCustomerIds = Object.keys(rowSelection).filter(
    (id) => rowSelection[id]
  );

  const clearSelection = () => setRowSelection({});

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    update({ query: "search", value: e.target.value });
  };

  const handleCustomerRowClick = (rowUuid: string) => {
    history.push(`${url}/${rowUuid}${search}`);
    trackEvent(CLICKED_CUSTOMER_LINE_ITEM_IN_TABLE);
  };

  const isFillterApplied =
    filterValues.SOURCES.length > 0 ||
    filterValues.MIN_AMOUNT_PAID ||
    filterValues.MAX_AMOUNT_PAID ||
    filterValues.MIN_AMOUNT_DUE ||
    filterValues.MAX_AMOUNT_DUE ||
    filterValues.MIN_TOTAL_INVOICED ||
    filterValues.MAX_TOTAL_INVOICED ||
    filterValues.HAS_OVERDUE ||
    filterValues.NO_OUTSTANDING_DUE ||
    filterValues.NO_PAYMENTS;

  const customersMissingAddress =
    invoiceCustomers?.customers_missing_address || [];
  const customersMissingAddressCount =
    invoiceCustomers?.customers_missing_address_count || 0;
  const [isMissingAddressBannerDismissed, setIsMissingAddressBannerDismissed] =
    useState(false);
  const addCustomerAddressesModal = useModal();

  return (
    <div className="t-text-text-60 t-flex t-gap-3 t-flex-col t-relative">
      {!isMissingAddressBannerDismissed && customersMissingAddressCount > 0 && (
        <Banner
          type="info"
          icon={
            <span className="t-flex t-shrink-0 t-self-center t-text-blue-80">
              <Info color="currentColor" stroke="1.2" size="16" />
            </span>
          }
          description={`${pluralize(
            customersMissingAddressCount,
            "customer",
            "customers"
          )} ${
            customersMissingAddressCount === 1 ? "is" : "are"
          } missing billing address. Add their addresses to accurately track sales tax thresholds and calculate tax for their invoices.`}
          cta={
            <Button
              customType="secondary"
              size="small"
              onClick={addCustomerAddressesModal.open}
            >
              Add details
            </Button>
          }
          onClose={() => setIsMissingAddressBannerDismissed(true)}
        />
      )}
      <AddCustomerAddressesModal
        open={addCustomerAddressesModal.isOpen}
        onClose={addCustomerAddressesModal.close}
        customers={customersMissingAddress}
        groupId={groupId!}
        entityId={entityId!}
      />
      <div className="t-flex t-gap-5 t-flex-col t-sticky t-top-0 t-z-header t-bg-white">
        <div className="t-flex t-gap-4 t-mt-2 sm:t-flex-col sm:t-gap-2">
          <div className="t-flex t-gap-2 t-w-full t-items-center">
            <Search
              isLoading={isFetching}
              className="t-w-[18%] focus:t-w-3/6 t-transition-all sm:t-w-auto sm:t-min-w-0 sm:t-flex-1 sm:focus:t-w-auto"
              defaultValue={searchValue || ""}
              onChange={onSearch}
              placeholder="Search"
            />
            <CustomerFilter filter={filterValues} updateFilter={updateFilter} />
          </div>
          <div className="t-flex t-justify-end t-self-start sm:t-self-end">
            <PaginationHandler
              page_num={Number(page) || 1}
              per_page={invoiceCustomers?.per_page ?? 0}
              total_count={invoiceCustomers?.total_count ?? 0}
              total_pages={invoiceCustomers?.total_pages ?? 0}
              isLoading={isFetching}
            />
          </div>
        </div>
      </div>
      <Async.Root
        isLoading={loadingCustomers}
        isSuccess={loadedCustomers}
        isEmpty={customers?.length === 0}
      >
        <Async.Success>
          <TableUI table={table} size="small">
            {({ row }) => (
              <Table.Row
                key={row.id}
                onRowClick={() => handleCustomerRowClick(row.original.uuid)}
                className="t-cursor-pointer"
                tabIndex={0}
                role="button"
              >
                {row.getVisibleCells().map((cell) => {
                  return (
                    <Table.Cell
                      key={cell.id}
                      style={{ width: `${cell.column.getSize()}%` }}
                      aria-label={cell.column.id}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Table.Cell>
                  );
                })}
              </Table.Row>
            )}
          </TableUI>
        </Async.Success>
        <Async.Empty>
          <div className="t-flex t-flex-col t-items-center t-justify-center t-h-72 t-text-text-60 t-gap-3">
            <EmptyInvoiceList />
            {searchValue && (
              <>
                <div className="t-flex t-flex-col t-gap-2 t-justify-center t-items-center">
                  <p className="t-m-0 t-text-subtitle-sm">
                    No results found for your search
                  </p>
                  <p className="t-m-0 t-text-body-sm">
                    Try clearing the search.
                  </p>
                </div>
                <Button
                  onClick={() => update({ query: "search", value: "" })}
                  size="small"
                  customType="primary"
                >
                  Clear search
                </Button>
              </>
            )}

            {isFillterApplied && (
              <>
                <div className="t-flex t-flex-col t-gap-2 t-justify-center t-items-center">
                  <p className="t-m-0 t-text-subtitle-sm">No results found</p>
                  <p className="t-m-0 t-text-body-sm">
                    Try clearing the filter.
                  </p>
                </div>
                <Button
                  onClick={() => updateFilter("SOURCES", [])}
                  size="small"
                  customType="primary"
                >
                  Clear filter
                </Button>
              </>
            )}
            {!isFillterApplied && !searchValue && (
              <>
                <div className="t-flex t-flex-col t-gap-2 t-justify-center t-items-center">
                  <p className="t-m-0 t-text-subtitle-sm">Add first customer</p>
                  <p className="t-m-0 t-text-body-sm">
                    Add & manage receivables for customers
                  </p>
                </div>
                <LockedUi
                  feature={WRITE_PERMISSION_KEYS.REVENUE}
                  blockedUi={<LockedButton text="Add customer" />}
                >
                  <Button
                    onClick={() => setCustomerId("NEW")}
                    size="small"
                    customType="primary"
                  >
                    Add customer
                  </Button>
                </LockedUi>
              </>
            )}
          </div>
        </Async.Empty>
      </Async.Root>
      <InvoiceCustomerModal
        customerId={customerId}
        setCustomerId={setCustomerId}
      />
      <CustomerBulkActions
        entityId={entityId}
        groupId={groupId}
        selectedCustomerIds={selectedCustomerIds}
        onClearSelection={clearSelection}
      />
    </div>
  );
};
